import React, { useEffect, useState } from "react";
import TooltipDots from "../toolTipDots/toolTipDots";
import "./videoPlayer.scss";

const VideoMonitor = ({
  videoRef,
  src,
  title,
  autoPlay,
  onLoadedMetadata,
  className = "",
  showTitleOverlayLogic = true, // New prop to toggle title logic
  isAd = false, // New prop to identify if the video is an ad
  onClick,
  containerStyle = {}, // new prop with a default empty object
}) => {
  const [showTitleOverlay, setShowTitleOverlay] = useState(true);

  // Log initial props to verify ad vs. skit usage
  useEffect(() => {
    console.log("[DEBUG] VideoMonitor props:", { src, isAd });
  }, [src, isAd]);

  // Dynamic source injection with debugging
  useEffect(() => {
    if (videoRef.current && src) {
      try {
        const urlObj = new URL(src);
        const pathname = urlObj.pathname; // e.g., "/videos/…/video1759123923.mp4"
        const ext = pathname.split(".").pop().toLowerCase();
        console.log("[DEBUG] Extracted file extension:", ext);

        const mimeTypes = {
          mp4: "video/mp4",
          webm: "video/webm",
          ogv: "video/ogg",
          mov: "video/quicktime",
        };

        const mime = mimeTypes[ext];
        console.log("[DEBUG] Determined MIME type:", mime);

        if (!mime) {
          console.error("[DEBUG] Unsupported file extension:", ext);
          return;
        }

        const sourceHTML = `<source src="${src}" type="${mime}">`;
        console.log("[DEBUG] Setting video innerHTML to:", sourceHTML);
        videoRef.current.innerHTML = sourceHTML;
        console.log(
          "[DEBUG] Final video innerHTML:",
          videoRef.current.innerHTML
        );
        videoRef.current.load();
      } catch (error) {
        console.error("[DEBUG] Error parsing video URL:", error);
      }
    }
  }, [src, videoRef]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const handleTimeUpdate = () => {
      const currentTime = videoElement.currentTime;
      setShowTitleOverlay(currentTime === 0);
    };
    const handleMetadataLoaded = () => {
      if (videoElement && videoElement.duration) {
        if (typeof onLoadedMetadata === "function") {
          onLoadedMetadata(videoElement.duration);
        } else {
          console.warn(
            "[DEBUG] onLoadedMetadata callback is not provided or not a function."
          );
        }
      } else {
        console.warn("[DEBUG] Metadata loaded but duration is not available.");
      }
    };

    if (videoElement && src) {
      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.addEventListener("loadedmetadata", handleMetadataLoaded);
      return () => {
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener(
          "loadedmetadata",
          handleMetadataLoaded
        );
      };
    }
  }, [videoRef, onLoadedMetadata, src]);

  return (
    <div
      className={`video-monitor-container ${className}`}
      onClick={() => {
        if (isAd) {
          console.warn("Ad is playing. Navigation disabled.");
        } else if (typeof onClick === "function") {
          console.log("VideoMonitor: Triggering parent's onClick logic.");
          onClick(); // Call parent's navigation logic
        } else {
          console.error("onClick is not a function or missing.");
        }
      }} // Navigate when clicking the monitor container
      style={{
        position: "relative",
        width: "100%",
        height: 0,
        paddingBottom: "62.25%" /* 16:9 ratio */,
        cursor: isAd ? "default" : "pointer", // Disable pointer for ads
        ...containerStyle // override with custom style if provided
      }}
    >
      {showTitleOverlayLogic && showTitleOverlay && !isAd && (
        <div
          style={{
            position: "absolute",
            top: 0,
            marginTop: "10px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "var(--color-offwhite)",
            zIndex: 1,
            padding: "30px 0",
            width: "50%",
            fontSize: "1rem",
            textAlign: "center",
            WebkitBackdropFilter: "blur(10px)",
            backdropFilter: "blur(10px)",
            background: "rgba(0, 0, 0, 0.5)",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <TooltipDots text={title} maxLength={12} />
        </div>
      )}
      <video
        ref={videoRef}
        src={src}
        autoPlay={autoPlay}
        // Prevents iPhone from forcing fullscreen mode.
        // Allows video to play inside your webpage UI.
        playsInline
        webkit-playsinline
        preload="auto" // Ensures iOS starts loading immediately
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
        // The onError handler on the <video> tag logs errors if the src is invalid
        // The logic ensures that invalid URLs do not crash the app but provide meaningful errors
        onError={(e) => {
          console.error("Error loading video:", e.target.error);
        }}
      />
    </div>
  );
};

export default VideoMonitor;
